import { useCallback } from 'react'

import { ICheckoutProcessData, PurchaseFormEventDetails } from 'meta/pages/checkout'
import { QueryParams } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { AnalyticsContentType, AnalyticsEventType, trackFbPixelEvent } from 'utils/analytics/track'
import { CookieClient, CookieName } from 'utils/cookie'

import { checkIfTransactionIsGoodsRelated } from 'components/Pages/PurchaseForm/Tickets/utils'
import { useCheckoutProcess } from 'hooks/useCheckout'
import { useFacebookPixelId, useGoogleAnalyticsId, useProxyApiBase, useS2SToken } from 'hooks/useEnv'
import { useQueryParams } from 'hooks/useQueryParams'
import { useUserData } from 'hooks/useAuth'

const getGA4AddToCartData = (
  data: ICheckoutProcessData & { event: PurchaseFormEventDetails; transactionCode: string }
) => {
  const { cartItems, value, event, transactionCode } = data

  const isTransactionGoodsRelated = event && event.policy && checkIfTransactionIsGoodsRelated(event.policy?.code)

  const analyticsEventCategory = isTransactionGoodsRelated ? AnalyticsContentType.good : AnalyticsContentType.ticket

  return {
    'transaction_id': transactionCode,
    'event_label': event && event.id,
    'event_category': event && analyticsEventCategory,
    ticket_platform: "TicketSms",
    'items': cartItems.map((x) => {
      return {
        item_id: x.id,
        item_name: event && event.name ? event.name : 'Evento Sconosciuto',
        quantity: x.quantity,
      }
    }),
    'currency': 'EUR',
    'num_items': cartItems.reduce<number>((total, { quantity }) => total + quantity, 0),
    'value': (value / 100).toFixed(2),
  }
}

export const useTrackProcess = () => {
  const userData = useUserData()
  const ticketsmsFbPixelId = useFacebookPixelId()
  const proxyApiBase = useProxyApiBase()
  const googleAnalytics4Id = useGoogleAnalyticsId()
  const checkoutProcess = useCheckoutProcess()
  const { transactionCode } = useQueryParams<QueryParams['CheckoutProcess']>()
  const s2sToken = useS2SToken()

  return useCallback(() => {
    if (!checkoutProcess?.data || !checkoutProcess.data.cartItems) return

    const { data } = checkoutProcess
    const { cartItems, fbPixelId: orgFbPixel, googleTags, value, event } = data

    const isTransactionGoodsRelated = event && event.policy && checkIfTransactionIsGoodsRelated(event.policy?.code)

    const analyticsEventCategory = isTransactionGoodsRelated ? AnalyticsContentType.good : AnalyticsContentType.ticket

    //@ts-ignore
    if (window && window.fbq) {
      //@ts-ignore
      window.fbq('init', orgFbPixel ? orgFbPixel : ticketsmsFbPixelId)

      const lastTransaction = CookieClient.get(CookieName.LAST_TRANSACTION)

      if (lastTransaction !== transactionCode) {
        trackFbPixelEvent(
          AnalyticsEventType.Purchase,
          {
            'content_ids': event && [event.id],
            'content_name': `Event purchase completed (${transactionCode})`,
            'content_type': analyticsEventCategory,
            'contents': cartItems.map((x) => ({ id: x.id, quantity: x.quantity })),
            'currency': 'EUR',
            'num_items': cartItems.reduce<number>((total, { quantity }) => total + quantity, 0),
            'value': (value / 100).toFixed(2),
          },
          {
            route: ApiEndpoint.analytics.transaction(transactionCode),
            fromOrganizer: orgFbPixel !== ticketsmsFbPixelId,
            s2sToken: s2sToken,
          },
          userData
        )

        //@ts-ignore
        if (window.gtag) {
          if (googleTags) {
            googleTags.map((elem) => {
              //@ts-ignore
              window.gtag('config', elem)
            })
          }

          const ga4AddToCartData = getGA4AddToCartData({ ...data, transactionCode })
          //@ts-ignore
          window.gtag('event', 'purchase', ga4AddToCartData)
        }

        //@ts-ignore
        window.ttq &&
          //@ts-ignore
          window.ttq.track('PlaceAnOrder', {
            content_type: analyticsEventCategory,
            ticket_platform: "TicketSms",
            description: transactionCode,
            contents: cartItems.map((x) => ({
              content_id: x.id,
              quantity: x.quantity,
            })),
            currency: 'EUR',
            value: (value / 100).toFixed(2),
          })

        CookieClient.set(CookieName.LAST_TRANSACTION, transactionCode)
      }
    }
  }, [checkoutProcess, googleAnalytics4Id, proxyApiBase, ticketsmsFbPixelId, transactionCode])
}
